import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { PageLoader } from "../../components/PageLoader.tsx";
import {
  IconClock,
  IconAlertTriangle,
  IconChevronRight,
  IconTicket,
} from "../../components/Icons.tsx";
import { useOrder, useCancelOrder, useContactManager } from "@/features/orders/hooks";
import { useProceedToPayment } from "@/features/payment/hooks";
import {
  getTelegram,
  haptic,
  safeShowAlert,
  safeShowConfirm,
} from "../../lib/telegram.ts";
import { useSafeTimeout } from "../../lib/hooks";
import { getErrorMessage } from "../../lib/graphql.ts";

const CARD_CLASS =
  "rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50";

const CONTACT_SECTION_ID = "order-contact-manager";

export function OrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const tg = getTelegram();

  // Safe timeout for scroll handling
  const { setTimeout } = useSafeTimeout();

  // URQL hooks
  const { order, isLoading, error, refetch } = useOrder(orderId ?? "");
  const { proceedToPayment, isLoading: isProceeding } = useProceedToPayment();
  const { cancelOrder, isLoading: isCancelling } = useCancelOrder();
  const { contactManager, isLoading: isSendingMessage } = useContactManager();

  const [contactMessage, setContactMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Прокрутка к переписке при переходе по ссылке с #contact (кнопка «Ответить» в сообщении)
  useEffect(() => {
    const hash = location.hash?.toLowerCase();
    const params = new URLSearchParams(location.search);
    if (hash === "#contact" || params.get("open") === "contact") {
      requestAnimationFrame(() => {
        document.getElementById(CONTACT_SECTION_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash, location.search]);

  const handleProceedToPayment = useCallback(async () => {
    if (!orderId || isProceeding) return;

    try {
      await proceedToPayment(orderId);
      haptic("success");
      navigate(`/payment/${orderId}`);
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Не удалось перейти к оплате"));
    }
  }, [orderId, isProceeding, navigate, proceedToPayment]);

  const handleCancelOrder = useCallback(async () => {
    if (!orderId || isCancelling) return;

    const confirmMessage = "Вы уверены, что хотите отменить заказ?";

    const performCancel = async () => {
      haptic("warning");

      try {
        await cancelOrder(orderId);
        haptic("success");

        const message = "Заказ отменён";
        safeShowAlert(message);

        navigate("/orders");
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Не удалось отменить заказ"));
      }
    };

    safeShowConfirm(confirmMessage, (confirmed: boolean) => {
      if (confirmed) performCancel();
    });
  }, [orderId, isCancelling, navigate, cancelOrder]);

  const handleContactManager = useCallback(async () => {
    if (!orderId || isSendingMessage || !contactMessage.trim()) return;

    try {
      await contactManager(orderId, contactMessage.trim());
      haptic("success");
      safeShowAlert("Сообщение отправлено! Менеджер свяжется с вами.");
      setContactMessage("");
      // Refetch order to show the new message in the thread
      refetch();
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Не удалось отправить сообщение"));
    }
  }, [orderId, isSendingMessage, contactMessage, contactManager, refetch]);

  // Redirect to payment page if status is AWAITING_PAYMENT
  useEffect(() => {
    if (order?.status === "AWAITING_PAYMENT") {
      navigate(`/payment/${orderId}`, { replace: true });
    }
  }, [order, orderId, navigate]);

  // Setup BackButton
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  const currencySymbol =
    order?.currency === "THB"
      ? "฿"
      : order?.currency === "RUB"
        ? "₽"
        : (order?.currency ?? "");

  if (isLoading) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  const errorMessage = error?.message ?? null;

  if (errorMessage || !order) {
    return (
      <Layout>
        <div className={`${CARD_CLASS} p-5 text-center`}>
          <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 md-mobile:w-14 md-mobile:h-14 lg-mobile:w-16 lg-mobile:h-16 mx-auto mb-3 xs:mb-4 lg-mobile:mb-5 text-amber-500" />
          <h2 className="text-lg xs:text-xl md-mobile:text-2xl lg-mobile:text-[1.75rem] font-bold text-slate-900 mb-2 lg-mobile:mb-3">
            Ошибка
          </h2>
          <p className="text-slate-600 mb-4 xs:mb-6 lg-mobile:mb-8 text-sm md-mobile:text-base lg-mobile:text-lg">
            {errorMessage ?? "Заказ не найден"}
          </p>
          <button onClick={() => navigate("/")} className="btn-primary w-full">
            На главную
          </button>
        </div>
      </Layout>
    );
  }


  return (
    <Layout>
      {/* Timer for expiration */}
      {order.expiresAt && order.status === "CREATED" && (
        <div className="rounded-2xl overflow-hidden bg-amber-50/95 shadow-sm border border-amber-200/60 ring-1 ring-amber-200/30 p-3 mb-4 lg-mobile:mb-6">
          <p className="text-[13px] text-amber-800 flex items-center justify-center gap-2 font-medium">
            <IconClock className="w-4 h-4 shrink-0" />
            Истекает через {getTimeRemaining(order.expiresAt)}
          </p>
        </div>
      )}

      {/* Pending verification */}
      {order.status === "PENDING_VERIFICATION" && (
        <div className="rounded-2xl overflow-hidden bg-purple-50/95 shadow-sm border border-purple-200/60 ring-1 ring-purple-200/30 p-4 mb-4 lg-mobile:mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
              <IconClock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-900 text-[15px]">Чек на проверке</h3>
              <p className="text-[13px] text-purple-600">Ожидайте подтверждения</p>
            </div>
          </div>
          <p className="text-[13px] text-purple-700">
            Мы проверяем ваш платёж. Обычно это занимает несколько минут. После подтверждения билеты будут отправлены в Telegram.
          </p>
        </div>
      )}

      {/* Мероприятие + Состав заказа — единая карточка */}
      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 mb-4 lg-mobile:mb-6">
        <Link
          to={`/event/${order.event.id}`}
          className="flex items-center gap-3 p-4 xs:p-5 md-mobile:p-6 min-h-[56px] active:bg-slate-50 [-webkit-tap-highlight-color:transparent] no-underline text-inherit"
        >
          <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
            {(order.event as { imageUrl?: string | null }).imageUrl ? (
              <img
                src={(order.event as { imageUrl: string }).imageUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100/90 to-slate-100/90 text-slate-400 text-2xl">
                ★
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-[15px] text-slate-900 truncate">
              {order.event.title}
            </h2>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {new Date(order.event.startsAt).toLocaleDateString("ru-RU", {
                weekday: "short",
                day: "numeric",
                month: "long",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
          <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
        </Link>

        <div className="border-t border-slate-100">
          <ul className="divide-y divide-slate-100">
          {order.items.map((item) => {
            const itemSubtotal = parseFloat(item.unitPrice) * item.quantity;
            return (
              <li
                key={item.id}
                className="flex justify-between items-center px-4 xs:px-5 md-mobile:px-6 py-3 min-h-[48px]"
              >
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-[15px] text-slate-900">
                    {item.ticketType.name}
                  </span>
                  <span className="text-slate-400 ml-1.5 text-[13px]">× {item.quantity}</span>
                </div>
                <span className="font-semibold text-[15px] text-slate-900 shrink-0">
                  {itemSubtotal} {currencySymbol}
                </span>
              </li>
            );
          })}
        </ul>
          <div className="px-4 xs:px-5 md-mobile:px-6 py-3 xs:py-4 border-t border-slate-100 space-y-1.5">
            {order.subtotal && (
              <div className="flex justify-between text-[13px] text-slate-600">
                <span>Билеты</span>
                <span>{order.subtotal} {currencySymbol}</span>
              </div>
            )}
            {order.fees && order.fees.length > 0 && order.fees.map((fee) => (
              <div key={fee.id} className="flex justify-between text-[13px] text-slate-500">
                <span>{fee.name}</span>
                <span>{fee.amount} {currencySymbol}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-200">
              <span className="font-medium text-[15px] text-slate-900">Итого</span>
              <span className="font-bold text-[17px] text-[var(--color-accent)]">
                {order.totalAmount} {currencySymbol}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Билеты — чипы, по тапу открытие страницы билета */}
      {order.tickets && order.tickets.length > 0 && (
        <div className={`${CARD_CLASS} p-4 xs:p-5 mb-4 lg-mobile:mb-6`}>
            <div className="flex flex-wrap gap-2">
              {order.tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => {
                    haptic("light");
                    navigate(`/ticket/${ticket.id}`);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-50/80 border border-teal-200/60 text-teal-700 transition-colors active:scale-[0.98]"
                >
                  <IconTicket className="w-4 h-4" />
                  <span className="text-[13px] font-medium">
                    №{ticket.ticketNumber}
                  </span>
                </button>
              ))}
            </div>
          </div>
      )}

      {/* Payment button */}
      {order.status === "CREATED" && (
        <button
          onClick={handleProceedToPayment}
          disabled={isProceeding}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {isProceeding ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Загрузка...</span>
            </>
          ) : (
            <span>Перейти к оплате</span>
          )}
        </button>
      )}

      {/* Переписка с менеджером — id для диплинка «Ответить» (#contact) */}
      <div id={CONTACT_SECTION_ID} className={`${CARD_CLASS} p-4 xs:p-5 mb-4 lg-mobile:mb-6`}>
        <h3 className="text-[15px] font-semibold text-slate-900 mb-3">
          Переписка по заказу
        </h3>
        <div className="space-y-3">
          {order.conversationMessages.length === 0 ? (
            <p className="text-sm text-slate-500">
              Сообщений пока нет. Напишите ниже — менеджер получит уведомление и ответ придёт сюда.
            </p>
          ) : (
            order.conversationMessages.map((msg) => {
              const fromUser = msg.isFromUser;
              return (
                <div
                  key={msg.id}
                  className={`flex flex-col gap-0.5 ${
                    fromUser ? "items-end" : "items-start"
                  }`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-xl text-[13px] ${
                      fromUser
                        ? "bg-[var(--color-accent)]/15 text-slate-800"
                        : "bg-slate-200/80 text-slate-800"
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">
                      {msg.messageText}
                    </p>
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {new Date(msg.createdAt).toLocaleString("ru-RU", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {fromUser ? " · Вы" : " · Менеджер"}
                  </span>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Поле ввода — всегда видно, отправка уведомляет админов и добавляет сообщение в переписку */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <textarea
            ref={textareaRef}
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
            onFocus={() => {
              // Scroll textarea above keyboard when it appears (especially for iOS)
              const scrollToInput = () => {
                const el = textareaRef.current;
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
                // If textarea is below the middle of visible area, scroll it up
                if (rect.bottom > viewportHeight - 50) {
                  const scrollAmount = rect.bottom - viewportHeight + 150;
                  window.scrollBy({ top: scrollAmount, behavior: "smooth" });
                }
              };
              // Multiple attempts to handle keyboard animation
              setTimeout(scrollToInput, 100);
              setTimeout(scrollToInput, 300);
              setTimeout(scrollToInput, 500);
            }}
            placeholder="Напишите сообщение менеджеру..."
            className="w-full min-h-[88px] p-3 text-sm xs:text-base text-slate-900 placeholder:text-slate-400 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent"
            maxLength={1000}
          />
          <button
            type="button"
            onClick={handleContactManager}
            disabled={isSendingMessage || !contactMessage.trim()}
            className="w-full mt-3 py-2.5 xs:py-3 text-sm xs:text-base font-medium text-white bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] disabled:opacity-50 rounded-xl transition-colors flex items-center justify-center gap-2 active:scale-[0.98]"
          >
            {isSendingMessage ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Отправка...
              </>
            ) : (
              "Отправить"
            )}
          </button>
        </div>
      </div>

      {["CREATED", "AWAITING_PAYMENT"].includes(order.status) && (
        <button
          onClick={handleCancelOrder}
          disabled={isCancelling}
          className="w-full mt-3 xs:mt-4 py-2.5 px-4 rounded-xl text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 active:scale-[0.98] border border-red-200"
        >
          {isCancelling ? (
            <>
              <div className="w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
              Отмена...
            </>
          ) : (
            "Отменить заказ"
          )}
        </button>
      )}
    </Layout>
  );
}

function getTimeRemaining(expiresAt: string): string {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "истёк";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours} ч ${minutes} мин`;
  }
  return `${minutes} мин`;
}
